// 로그인 기억(2026-09-27 사용자 요청):
//   ① **자동 로그인** — 통합앱에서 마지막 로그인 후 7일 안에 다시 켜면 로그인 화면 없이 들어간다.
//      로그인 유지 토큰(회전형 refresh)은 **네이티브 쪽이 보관**한다(Rust `saved_login.rs` — Windows DPAPI · macOS 0600).
//      웹 저장소(localStorage)에 두지 않는 이유: 토큰은 비밀이고, 웹 저장소는 평문이다.
//   ② **마지막 로그인 ID 자동 채움** — ID 는 비밀이 아니므로 웹 저장소. 비밀번호는 어디에도 저장하지 않는다.
//      2026-09-28: 1개가 아니라 **최근 목록**(이메일 자동완성) — 저장·추천 규칙은 `../loginEmails.ts`.
//   ③ **브라우저 새로고침 유지**(2026-10-12 사용자 요청 — "새로고침하면 로그아웃된다"): 브라우저 뷰어는 토큰을
//      **탭 전용 저장소(sessionStorage)** 에 둔다 → 새로고침은 유지, **탭·브라우저를 닫으면 사라진다**(사용자 결정).
//      localStorage(닫아도 남음)는 쓰지 않는다 — 공용 PC 에 로그인이 남는다. 7일 유지는 여전히 통합앱만.
//      ⚠️ 브라우저 "탭 복제" 는 sessionStorage 를 복사한다 → 두 탭이 같은 회전형 토큰을 쓰다 한쪽이 갱신하면 서버가
//      재사용으로 보고 둘 다 끊는다(안전한 쪽 실패 — 다시 로그인하면 된다).
import { invoke } from '@tauri-apps/api/core';
import { loadLoginEmails, rememberLoginEmail } from '../loginEmails';

/** 통합앱(Tauri) 안인가 — 통합앱은 네이티브 보관(7일), 브라우저는 탭 전용 저장소(탭을 닫을 때까지). */
function inTauri(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

/** 브라우저 탭 전용 보관 키(sessionStorage). 값 = `SavedLogin` JSON. */
const TAB_LOGIN_KEY = 'rs.tabLogin';

/** 탭 저장소(막혀 있으면 null — 그때는 종전처럼 새로고침하면 로그아웃). */
function tabStorage(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** 브라우저: 탭 보관본 읽기(형식이 깨졌으면 지우고 null). */
function loadTabLogin(): SavedLogin | null {
  const s = tabStorage();
  const raw = s?.getItem(TAB_LOGIN_KEY);
  if (!s || !raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<SavedLogin>;
    if (typeof v.server === 'string' && typeof v.refreshToken === 'string' && v.refreshToken) {
      return { server: v.server, refreshToken: v.refreshToken };
    }
  } catch {
    /* 아래에서 지운다 */
  }
  s.removeItem(TAB_LOGIN_KEY);
  return null;
}

/** 브라우저: 탭 보관본 쓰기/지우기(빈 토큰 = 지우기). 저장소 오류는 삼킨다(유지 기능만 빠진다). */
function storeTabLogin(server: string, refreshToken: string): void {
  const s = tabStorage();
  if (!s) return;
  try {
    if (refreshToken) s.setItem(TAB_LOGIN_KEY, JSON.stringify({ server, refreshToken }));
    else s.removeItem(TAB_LOGIN_KEY);
  } catch {
    /* 용량·정책 — 무시 */
  }
}

/** 네이티브 보관본(Rust `SavedLogin` 과 같은 모양). */
export interface SavedLogin {
  server: string;
  refreshToken: string;
}

/** 보관된 로그인(통합앱 = 7일 안 네이티브 보관본 · 브라우저 = 이 탭 보관본). 없으면 null. */
export async function loadSavedLogin(): Promise<SavedLogin | null> {
  if (!inTauri()) return loadTabLogin();
  try {
    return (await invoke<SavedLogin | null>('saved_login_load')) ?? null;
  } catch {
    return null;
  }
}

/**
 * 로그인 유지 토큰이 바뀔 때마다 부른다(`Api.onRefreshTokenChanged`). 빈 문자열이면 보관본을 지운다.
 * @param server 토큰을 준 서버 — 다음 실행 때 서버가 바뀌었으면 쓰지 않는다.
 */
export function persistRefreshToken(server: string, refreshToken: string): void {
  if (!inTauri()) {
    storeTabLogin(server, refreshToken);
    return;
  }
  const p = refreshToken
    ? invoke('saved_login_store', { server, refreshToken })
    : invoke('saved_login_clear');
  void p.catch(() => {});
}

/** 마지막으로 로그인에 성공한 이메일(없으면 ''). 목록의 맨 앞 — `loginEmails.ts`. */
export function loadLastLoginId(): string {
  return loadLoginEmails()[0] ?? '';
}

/** 로그인 성공 — 자동완성 목록 맨 앞에 둔다(2026-09-28 부터 1개가 아니라 목록). 저장소가 막혔으면 자동 채움만 빠진다. */
export function saveLastLoginId(loginId: string): void {
  rememberLoginEmail(loginId);
}
